/**
 * What-if impact analysis panel (B3-a). Lets an admin preview the blast
 * radius of a config change BEFORE saving it — deleting a node, adding a
 * rule, or changing an SLA — via the read-only simulator. Renders the
 * ImpactReport (severity + findings + suggestions).
 */

import { useState } from 'react';
import { Button, Card, Spinner } from '@centraoneplus/ui';
import { ImpactFinding, useSimulateChange } from './hooks';
import { ThemedSelect } from '@components/common/ThemedSelect';

interface Props {
  tenantId: string;
}

type Kind = 'delete_node' | 'sla_change' | 'add_rule';

const NODE_TYPES = [
  'PIPELINE',
  'PIPELINE_STAGE',
  'TICKET_TYPE',
  'SLA_POLICY',
  'AUTOMATION_RULE',
  'FIELD_DEFINITION',
];

const SEV_STYLES: Record<string, string> = {
  breaking: 'bg-red-100 text-red-800',
  warning: 'bg-amber-100 text-amber-800',
  info: 'bg-sky-100 text-sky-800',
  none: 'bg-emerald-100 text-emerald-800',
};

const SEV_LABEL: Record<string, string> = {
  breaking: 'Critical',
  warning: 'Needs attention',
  info: 'Info',
  none: 'No issues',
};

export default function OrgBrainSimulate({ tenantId }: Props) {
  const sim = useSimulateChange(tenantId);
  const [kind, setKind] = useState<Kind>('delete_node');
  const [nodeType, setNodeType] = useState('PIPELINE_STAGE');
  const [sourceId, setSourceId] = useState('');
  const [firstResponse, setFirstResponse] = useState('');
  const [resolution, setResolution] = useState('');
  const [ruleJson, setRuleJson] = useState('{\n  "triggerType": "ticket.created"\n}');
  const [error, setError] = useState<string | null>(null);

  async function handleSimulate() {
    setError(null);
    let body: Record<string, unknown>;
    try {
      if (kind === 'delete_node') {
        if (!sourceId.trim()) throw new Error('Enter the source id of the node to delete.');
        body = { kind, node_type: nodeType, source_id: sourceId.trim() };
      } else if (kind === 'sla_change') {
        if (!sourceId.trim()) throw new Error('Enter the SLA policy source id.');
        const targets: Record<string, number> = {};
        if (firstResponse) targets.firstResponseTarget = Number(firstResponse);
        if (resolution) targets.resolutionTarget = Number(resolution);
        body = { kind, policy_source_id: sourceId.trim(), new_targets: targets };
      } else {
        body = { kind, rule: JSON.parse(ruleJson) };
      }
    } catch (e: any) {
      setError(e?.message || 'Invalid input');
      return;
    }
    try {
      await sim.mutateAsync(body);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Simulation failed');
    }
  }

  const report = sim.data;

  return (
    <Card className="p-6 space-y-4">
      <header>
        <h2 className="text-lg font-semibold">What-if impact analysis</h2>
        <p className="text-sm text-gray-600 mt-1 max-w-xl">
          Preview what a change would affect before you make it — nothing is
          saved.
        </p>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">
          <div className="text-gray-600 text-xs mb-1">Change type</div>
          <ThemedSelect
            aria-label="Change type"
            value={kind}
            onChange={(e) => setKind(e.target.value as Kind)}
            className="border rounded px-2 py-1.5"
          >
            <option value="delete_node">Delete something</option>
            <option value="sla_change">Change an SLA</option>
            <option value="add_rule">Add an automation rule</option>
          </ThemedSelect>
        </label>

        {kind === 'delete_node' && (
          <>
            <label className="text-sm">
              <div className="text-gray-600 text-xs mb-1">What</div>
              <ThemedSelect
                aria-label="Node type"
                value={nodeType}
                onChange={(e) => setNodeType(e.target.value)}
                className="border rounded px-2 py-1.5"
              >
                {NODE_TYPES.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </ThemedSelect>
            </label>
            <label className="text-sm flex-1 min-w-[200px]">
              <div className="text-gray-600 text-xs mb-1">Source id</div>
              <input
                aria-label="Source id"
                value={sourceId}
                onChange={(e) => setSourceId(e.target.value)}
                placeholder="e.g. the stage/pipeline id"
                className="border rounded px-2 py-1.5 w-full"
              />
            </label>
          </>
        )}

        {kind === 'sla_change' && (
          <>
            <label className="text-sm flex-1 min-w-[180px]">
              <div className="text-gray-600 text-xs mb-1">SLA policy source id</div>
              <input
                aria-label="SLA policy source id"
                value={sourceId}
                onChange={(e) => setSourceId(e.target.value)}
                className="border rounded px-2 py-1.5 w-full"
              />
            </label>
            <label className="text-sm">
              <div className="text-gray-600 text-xs mb-1">First response (min)</div>
              <input
                type="number"
                aria-label="First response target"
                value={firstResponse}
                onChange={(e) => setFirstResponse(e.target.value)}
                className="border rounded px-2 py-1.5 w-28"
              />
            </label>
            <label className="text-sm">
              <div className="text-gray-600 text-xs mb-1">Resolution (min)</div>
              <input
                type="number"
                aria-label="Resolution target"
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                className="border rounded px-2 py-1.5 w-28"
              />
            </label>
          </>
        )}

        <Button onClick={handleSimulate} disabled={sim.isPending}>
          {sim.isPending ? 'Simulating…' : 'Simulate'}
        </Button>
      </div>

      {kind === 'add_rule' && (
        <label className="text-sm block">
          <div className="text-gray-600 text-xs mb-1">
            Rule details (advanced — paste the rule as JSON)
          </div>
          <textarea
            aria-label="Rule JSON"
            value={ruleJson}
            onChange={(e) => setRuleJson(e.target.value)}
            rows={5}
            className="border rounded px-2 py-1.5 w-full font-mono text-xs"
          />
        </label>
      )}

      {sim.isPending && <Spinner />}

      {report && !sim.isPending && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <span
              className={`inline-block px-2 py-0.5 text-xs rounded font-medium ${
                SEV_STYLES[report.severity] ?? 'bg-gray-100 text-gray-700'
              }`}
            >
              {SEV_LABEL[report.severity] ?? report.severity}
            </span>
            <span className="text-gray-900">{report.summary}</span>
          </div>
          {report.findings.length > 0 && (
            <ul className="space-y-1">
              {report.findings.map((f: ImpactFinding, i: number) => (
                <li key={i} className="text-sm border border-gray-200 rounded p-2">
                  <span
                    className={`inline-block px-1.5 py-0.5 text-xs rounded mr-2 ${
                      SEV_STYLES[f.severity] ?? 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {SEV_LABEL[f.severity] ?? f.severity}
                  </span>
                  {f.message}
                </li>
              ))}
            </ul>
          )}
          {report.suggestions?.length > 0 && (
            <div className="text-xs text-gray-600">
              Suggestions: {report.suggestions.join('; ')}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="text-sm text-red-700 bg-red-50 rounded px-3 py-2">{error}</div>
      )}
    </Card>
  );
}